import React, { useEffect, useMemo, useRef, useState } from "react";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import { BaseSelect } from "Components/Base/BaseSelect";
import { checkStatusCodeSuccess, notFound } from "Components/constants/Common";
import {
  AddPartIssueDetailsLabel,
  EditPartIssueDetailsLabel,
  viewPartIssueDetailsLabel,
} from "Components/constants/PartsIssueDetails";
import {
  InputPlaceHolder,
  SelectPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import { useFormik } from "formik";
import { submitLabel, updateLabel } from "Components/constants/Role";
import { getRONumberList } from "Api/RepairOrderOpen";
import { createPID, viewPID } from "Api/PartIssueDetails";
import { toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import Loader from "Components/Base/BaseLoader";
import * as yup from "yup";
import {
  ALL_FIELDS_ARE_REQUIRED,
  AT_LEAST_ONE_VALID_ISSUE_QTY,
  DOCUMENT_DATE_LABEL,
  HEADER_TEXT_LABEL,
  ISSUE_DATE_LABEL,
  ISSUE_QTY_CANNOT_GREATER_PENDING_QTY,
  ISSUE_QTY_LABEL,
  ISSUE_SLIP_NO_LABEL,
  ISSUE_TYPE_LABEL,
  ITEM_CODE_LABEL,
  MATERIAL_DESC_LABEL,
  MATERIAL_GRP_DESC_LABEL,
  MATERIAL_GRP_LABEL,
  MATERIAL_TYPE_LABEL,
  PENDING_QTY_LABEL,
  PLEASE_ENTER_PROD_DETAILS,
  PROJECT_NAME_LABEL,
  QTY_GREATER_THAN_ZERO,
  RACK_LOCA_LABEL,
  RECEIVERS_NAME,
  REQUESTED_QTY_LABEL,
  RETURN_SPARE_PART,
  RO_NO_LABEL,
  SR_NO_LABEL,
  STORE_LOCA_LABEL,
  TEXT_DOCUMENT_OR_REMARKS,
  UOM_LABEL,
} from "Components/constants/ROModule";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const PartsIssueDetails = () => {
  const [loader, setLoader] = useState<boolean>(false);
  const [PID, setPID] = useState<any[]>([]);
  const [selectedRONo, setSelectedRONo] = useState<string | null>(null);
  const gridRef = useRef<AgGridReact<any>>(null);
  const [btnLoader, setBtnLoader] = useState(false);
  const [RONumberList, setRONumberList] = useState<any[]>([]);
  const { id } = useParams();
  const location = useLocation();
  const isEditPIDRoute = location.pathname.includes("editPartIssueDetailsForm");
  const isViewPIDRoute = location.pathname.includes("viewPartIssueDetailsForm");
  const isViewMode = location.pathname.includes("viewPartIssueDetailsForm");
  const getPageTitle = () => {
    if (isViewPIDRoute) return viewPartIssueDetailsLabel;
    if (isEditPIDRoute) return EditPartIssueDetailsLabel;
    if (isViewMode) return viewPartIssueDetailsLabel;
    return AddPartIssueDetailsLabel;
  };

  const navigate = useNavigate();

  const validationSchema = yup.object({
    SelectRONo: yup.string().required(validationMessages.required(RO_NO_LABEL)),
    documentDate: yup
      .date()
      .required(validationMessages.required(DOCUMENT_DATE_LABEL)),
    header_text: yup
      .string()
      .required(validationMessages.required(HEADER_TEXT_LABEL)),
    issueDate: yup
      .date()
      .required(validationMessages.required(ISSUE_DATE_LABEL)),
    issueSlipNo: yup
      .string()
      .required(validationMessages.required(ISSUE_SLIP_NO_LABEL)),
  });

  const formik = useFormik({
    initialValues: {
      SelectRONo: "",
      documentDate: new Date().toISOString().split("T")[0],
      issueDate: "",
      header_text: "",
      issueSlipNo: "",
    },
    validationSchema,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  const handleSubmit = (values: any) => {
    if (!PID || PID.length === 0) {
      toast.error(PLEASE_ENTER_PROD_DETAILS);
      return;
    }

    const hasValidRow = PID.some(
      (row: any) =>
        row.issue_quantity > 0 &&
        row.issue_type &&
        row.rec_name &&
        row.return_spare_part
    );

    if (!hasValidRow) {
      toast.error(ALL_FIELDS_ARE_REQUIRED);
      return;
    }

    const validRows = PID.filter(
      (row: any) =>
        row.issue_quantity > 0 &&
        row.issue_type &&
        row.rec_name &&
        row.return_spare_part
    );

    const payload = {
      repair_order_id: selectedRONo,
      document_date: values.documentDate,
      issue_date: values.issueDate,
      header_text: values.header_text,
      issue_no: values.issueSlipNo,
      product: validRows.map((item: any) => ({
        id: item.id,
        material_description: item.material_description,
        issue_quantity: item.issue_quantity,
        pending_quantity: item.pending_quantity,
        issue_type: item.issue_type,
        rec_name: item.rec_name,
        return_spare_part: item.return_spare_part,
      })),
    };

    setBtnLoader(true);
    createPID(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          toast.success(res.message);
          navigate("/partIssueDetails");
        } else {
          toast.error(res.message);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      })
      .finally(() => {
        setBtnLoader(false);
      });
  };
  useEffect(() => {
    getRONumberList()
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          const data = res?.data;
          setRONumberList(
            data.map((item: any) => ({
              label: item.ro_no,
              value: item.id,
            }))
          );
        } else {
          console.error(res.message);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      });
  }, []);

  useEffect(() => {
    if (selectedRONo) {
      setLoader(true);
      setPID([]);
      viewPID(Number(selectedRONo))
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            const data = res?.data || [];
            if (data && data.length > 0) {
              const pidData = data[0];
              formik.setValues({
                SelectRONo: pidData.repair_order_id || "",
                documentDate: pidData.document_date || "",
                issueDate: pidData.issue_date || "",
                header_text: pidData.header_text || "",
                issueSlipNo: pidData.issue_no || "",
              });

              const gridData = pidData.partRequisitionItem.map((item: any) => ({
                id: item.id,
                item_code: item.item_code,
                material_description:
                  item.material_description || item.parts_name,
                uom: item.uom,
                qty: item.qty,
                issue_quantity: isViewPIDRoute ? item.issue_quantity : 0,
                pending_quantity: item.pending_quantity,
                store_location: item.store_location,
                project_name: item.project_name,
                material_type: item.material_type,
                material_group: item.material_group,
                material_group_description: item.material_group_description,
                issue_type: item.issue_type || "",
                rack_location: item.rack_location,
                rec_name: item.rec_name || "",
                remarks: item.remarks,
                return_spare_part: item.return_spare_part || "",
              }));

              setPID(gridData);
            }
          } else {
            toast.error(res.message);
          }
        })
        .catch((err) => {})
        .finally(() => {
          setLoader(false);
        });
    } else {
      setPID([]);
    }
  }, [selectedRONo]);

  useEffect(() => {
    if (id) {
      setLoader(true);
      setPID([]);
      viewPID(Number(id))
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            const data = res?.data || [];
            if (data && data.length > 0) {
              const pidData = data[0];
              formik.setValues({
                SelectRONo: pidData.repair_order_id || "",
                documentDate: pidData.document_date || "",
                issueDate: pidData.issue_date || "",
                header_text: pidData.header_text || "",
                issueSlipNo: pidData.issue_no || "",
              });
              setSelectedRONo(pidData.repair_order_id);
              const gridData = pidData.partRequisitionItem.map((item: any) => ({
                id: item.id,
                item_code: item.item_code,
                material_description:
                  item.material_description || item.parts_name,
                uom: item.uom,
                qty: item.qty,
                issue_quantity: isViewPIDRoute ? item.issue_quantity : 0,
                pending_quantity: item.pending_quantity,
                store_location: item.store_location,
                project_name: item.project_name,
                material_type: item.material_type,
                material_group: item.material_group,
                material_group_description: item.material_group_description,
                issue_type: item.issue_type || "",
                rack_location: item.rack_location,
                rec_name: item.rec_name || "",
                remarks: item.remarks,
                return_spare_part: item.return_spare_part || "",
              }));

              setPID(gridData);
            }
          } else {
            toast.error(res.message);
          }
        })
        .catch((err) => {})
        .finally(() => {
          setLoader(false);
        });
    } else {
      setPID([]);
    }
  }, [id]);

  const partsIssueColumns = useMemo(
    () => [
      {
        headerName: SR_NO_LABEL,
        headerTooltip: SR_NO_LABEL,
        valueGetter: "node.rowIndex + 1",
        field: "srNo",
        tooltipField: "srNo",
        flex: 1.5,
        minWidth: 150,
        editable: false,
        sortable: true,
      },
      {
        headerName: ITEM_CODE_LABEL,
        headerTooltip: ITEM_CODE_LABEL,
        field: "item_code",
        tooltipField: "item_code",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
      },
      {
        headerName: MATERIAL_DESC_LABEL,
        headerTooltip: MATERIAL_DESC_LABEL,
        field: "material_description",
        tooltipField: "material_description",
        editable: !isViewMode,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
      },
      {
        headerName: UOM_LABEL,
        headerTooltip: UOM_LABEL,
        field: "uom",
        flex: 1.5,
        minWidth: 150,
        tooltipField: "uom",
        editable: false,
        sortable: true,
      },
      {
        headerName: REQUESTED_QTY_LABEL,
        headerTooltip: REQUESTED_QTY_LABEL,
        field: "qty",
        tooltipField: "qty",
        editable: false,
        sortable: true,
        flex: 1.5,
        minWidth: 150,
        cellEditor: "agTextCellEditor",
        valueParser: (params: any) => {
          const value = params.newValue;
          return value ? parseFloat(value) : 0;
        },
      },
      {
        headerName: ISSUE_QTY_LABEL,
        headerTooltip: ISSUE_QTY_LABEL,
        field: "issue_quantity",
        tooltipField: "issue_quantity",
        editable: !isViewMode,
        required: true,
        flex: 1.5,
        headerClass: "required",
        minWidth: 150,
        sortable: true,
        cellEditor: "agTextCellEditor",
        valueParser: (params: any) => {
          const value = params.newValue;
          if (value > params.data.pending_quantity) {
            toast.error(ISSUE_QTY_CANNOT_GREATER_PENDING_QTY);
            return params.oldValue;
          }
          return value ? parseFloat(value) : 0;
        },
        onCellValueChanged: (event: any) => {
          const { oldValue, newValue, node } = event;
          const rowData = node.data;
          const pendingQty = rowData.pending_quantity;

          if (newValue <= pendingQty && newValue !== oldValue) {
            const difference = newValue - oldValue;
            const updatedPendingQty = pendingQty - difference;

            node.setDataValue("pending_quantity", updatedPendingQty);
          }
        },
      },
      {
        headerName: PENDING_QTY_LABEL,
        headerTooltip: PENDING_QTY_LABEL,
        field: "pending_quantity",
        tooltipField: "pending_quantity",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
      },
      {
        headerName: STORE_LOCA_LABEL,
        headerTooltip: STORE_LOCA_LABEL,
        field: "store_location",
        tooltipField: "store_location",
        editable: !isViewMode,
        sortable: true,
        flex: 1.5,
        minWidth: 150,
      },
      {
        headerName: PROJECT_NAME_LABEL,
        headerTooltip: PROJECT_NAME_LABEL,
        field: "project_name",
        tooltipField: "project_name",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
      },
      {
        headerName: MATERIAL_TYPE_LABEL,
        headerTooltip: MATERIAL_TYPE_LABEL,
        field: "material_type",
        tooltipField: "material_type",
        editable: false,
        sortable: true,
        flex: 1.5,
        minWidth: 150,
      },
      {
        headerName: MATERIAL_GRP_LABEL,
        headerTooltip: MATERIAL_GRP_LABEL,
        field: "material_group",
        tooltipField: "material_group",
        editable: false,
        sortable: true,
        flex: 1.5,
        minWidth: 150,
      },
      {
        headerName: MATERIAL_GRP_DESC_LABEL,
        headerTooltip: MATERIAL_GRP_DESC_LABEL,
        field: "material_group_description",
        tooltipField: "material_group_description",
        editable: false,
        sortable: true,
        flex: 1.5,
        minWidth: 150,
      },
      {
        headerName: ISSUE_TYPE_LABEL,
        headerTooltip: ISSUE_TYPE_LABEL,
        field: "issue_type",
        tooltipField: "issue_type",
        headerClass: "required",
        editable: !isViewMode,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: ["Debit", "Credit", "Warranty", "Return", "Other"],
        },
      },
      {
        headerName: RACK_LOCA_LABEL,
        headerTooltip: RACK_LOCA_LABEL,
        field: "rack_location",
        tooltipField: "rack_location",
        editable: !isViewMode,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
      },
      {
        headerName: RECEIVERS_NAME,
        headerTooltip: RECEIVERS_NAME,
        field: "rec_name",
        headerClass: "required",
        tooltipField: "rec_name",
        editable: !isViewMode,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
      },
      {
        headerName: TEXT_DOCUMENT_OR_REMARKS,
        headerTooltip: TEXT_DOCUMENT_OR_REMARKS,
        field: "remarks",
        tooltipField: "remarks",
        editable: false,
        sortable: true,
        flex: 1.5,
        minWidth: 150,
      },
      {
        headerName: RETURN_SPARE_PART,
        headerTooltip: RETURN_SPARE_PART,
        field: "return_spare_part",
        headerClass: "required",
        tooltipField: "return_spare_part",
        editable: !isViewMode,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
      },
    ],
    [isViewMode]
  );

  document.title = getPageTitle();

  return (
    <div className="page-content">
      {loader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <form onSubmit={formik.handleSubmit}>
          <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
            <span className="fs-4 d-flex gap-4">{getPageTitle()}</span>
            <button
              className="btn btn-danger"
              onClick={() => navigate("/partIssueDetails")}
            >
              <i className="mdi mdi-arrow-left" />
            </button>
          </div>
          <div className="card py-2 px-4 overflow-x-auto">
            <div className="d-flex flex-wrap justify-content-between mb-1 pe-auto">
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseSelect
                  name="SelectRONo"
                  label={RO_NO_LABEL}
                  options={RONumberList}
                  value={formik.values.SelectRONo}
                  handleChange={(name: any, value: any) => {
                    setSelectedRONo(value === "" ? null : value);
                    formik.setFieldValue(name, value);
                  }}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.SelectRONo}
                  touched={formik.touched.SelectRONo}
                  required
                  isDisabled={isViewMode}
                />
              </div>
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="documentDate"
                  placeholder={SelectPlaceHolder("")}
                  type="date"
                  label={DOCUMENT_DATE_LABEL}
                  value={formik.values.documentDate}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.documentDate}
                  touched={formik.touched.documentDate}
                  required
                  disabled={isViewMode}
                />
              </div>
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="header_text"
                  label={HEADER_TEXT_LABEL}
                  placeholder={InputPlaceHolder(HEADER_TEXT_LABEL)}
                  value={formik.values.header_text}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.header_text}
                  touched={formik.touched.header_text}
                  required
                  disabled={isViewMode}
                />
              </div>
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="issueDate"
                  placeholder={SelectPlaceHolder("")}
                  type="date"
                  label={ISSUE_DATE_LABEL}
                  value={formik.values.issueDate}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.issueDate}
                  touched={formik.touched.issueDate}
                  required
                  disabled={isViewMode}
                />
              </div>
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="issueSlipNo"
                  label={ISSUE_SLIP_NO_LABEL}
                  placeholder={InputPlaceHolder(ISSUE_SLIP_NO_LABEL)}
                  value={formik.values.issueSlipNo}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.issueSlipNo}
                  touched={formik.touched.issueSlipNo}
                  required
                  disabled={isViewMode}
                />
              </div>
            </div>

            <hr />
            <div className="table-css">
              <div className="ag-theme-quartz">
                <AgGridReact
                  ref={gridRef}
                  rowData={PID}
                  columnDefs={partsIssueColumns}
                  defaultColDef={{
                    sortable: true,
                    resizable: true,
                    flex: 1,
                    tooltipComponentParams: {
                      color: "blue",
                    },
                  }}
                  domLayout="normal"
                  getRowHeight={() => 35}
                  overlayNoRowsTemplate={notFound.dataNotFound}
                />
              </div>
            </div>

            <div className="d-flex justify-content-end mb-1 mt-4 w-100">
              <div className="col-12 d-flex justify-content-end mb-0">
                {!isViewMode && (
                  <BaseButton
                    type="submit"
                    label={id ? updateLabel : submitLabel}
                    color="success"
                    loader={btnLoader}
                    disable={btnLoader}
                  />
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PartsIssueDetails;
